import React, { useState, useRef, useEffect } from 'react';
import { stub as $t } from '@nbcnews/analytics-framework';
import Breakpoints from 'lib/Breakpoints';
import { getDataActivityMapID } from 'lib/articleUtils';
import { CarouselCard } from './CarouselCard';
import styles from './styles.module.scss';

$t('register', 'mbt_bacon_click', { allowDuplicate: true });

/**
 * @param {*} props - the properties object
 * @param {Array} props.items - array of article objects to be displayed in the carousel
 * @param {boolean} props.hasImages - flag indicating whether images should be displayed
 * @param {string} props.vertical - the vertical category of the articles, used for styling and fallback image selection
 * @param {string} props.title - the title of the carousel section
 * @param {string} props.activityMapComponentTitle - title for activity map tracking
 * @param {string} props.icid - tracking identifier for analytics
 * @param {object} props.presentationStyle - presentation style of curated list
 */
const Carousel = (props) => {
  const {
    items,
    hasImages,
    vertical,
    title,
    activityMapComponentTitle,
    icid,
    presentationStyle,
  } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  /**
   * Set isMobile in a way that prevents hydration issues
   */
  useEffect(() => {
    setIsMobile(Breakpoints.isS());

    /**
     * Handle window resize events to update isMobile state
     */
    const handleResize = () => {
      setIsMobile(Breakpoints.isS());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Touch/swipe handling
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const carouselRef = useRef(null);

  const SELECT_FALLBACK_IMG = 'https://media-cldnry.s-nbcnews.com/image/upload/rockcms/2022-10/nebula-cms-fallback-logos-select-c21d31.png';
  const TODAY_FALLBACK_IMG = 'https://media-cldnry.s-nbcnews.com/image/upload/rockcms/2025-08/nebula-cms-fallback-logos-today-27b942.jpg';

  const fallbackImg = vertical === 'select' ? SELECT_FALLBACK_IMG
    : TODAY_FALLBACK_IMG;


  const MAX_ARTICLES = 4;
  const articles = items.slice(0, MAX_ARTICLES);

  const totalSlides = Math.ceil(articles.length / 2);

  /**
   * @param {*} slideIndex
   */
  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  /**
   * Handle swipe navigation
   */
  const handleSwipe = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < totalSlides - 1) {
      // Swipe left - go to next slide
      goToSlide(currentSlide + 1);
    }

    if (isRightSwipe && currentSlide > 0) {
      // Swipe right - go to previous slide
      goToSlide(currentSlide - 1);
    }
  };

  /**
   * @param {object} e - touch event
   * Touch start event handler to record the starting X position
   */
  const onTouchStart = (e) => {
    touchEndX.current = null; // Reset end position
    touchStartX.current = e.targetTouches[0].clientX;
  };

  /**
   * @param {object} e - touch event
   * Touch move event handler to update the current X position
   */
  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  /**
   * Touch event handler to finalize the swipe action
   */
  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    handleSwipe();
  };


  /**
   * @returns {Array} - array of articles to be displayed on the current slide
   */
  const getCurrentSlideArticles = () => {
    if (!isMobile) {
      // Desktop: show up to 3 articles (hide 4th)
      return articles.slice(0, 3);
    }

    // Mobile
    return articles.slice(0, MAX_ARTICLES);
  };

  const currentArticles = getCurrentSlideArticles();

  // Build pages of 2 for mobile; desktop stays as-is
  const mobileSlides = [];
  for (let i = 0; i < currentArticles.length; i += 2) {
    mobileSlides.push(currentArticles.slice(i, i + 2));
  }

  // Though this component does not use the Bacon component, we are using
  // 'bacon' as the componentName for activity map tracking to align with
  // other curated list presentations that do use the Bacon component,
  // as per request from analytics team.
  const dataActivityMapID = getDataActivityMapID(
    {
      componentName: 'bacon',
      pageRegion: 'article-body',
      componentTitle: activityMapComponentTitle || '',
    },
  );

  return (
    <div className={styles.carouselContainer} data-testid="carousel-container" data-activity-map={dataActivityMapID}>
      <h2 className={styles.headline}>{title}</h2>
      <div
        className={styles.carousel}
        ref={carouselRef}
        data-slides={isMobile ? totalSlides : 1}
        style={
          isMobile
            ? { '--slide-index': currentSlide, '--slides': totalSlides }
            : undefined
        }
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchMove={isMobile ? onTouchMove : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
      >
        {!isMobile ? (
        // Desktop: no sliding, just first 3 as before
          currentArticles.map((article, idx) => (
            <CarouselCard
              key={article.id}
              article={article}
              fallbackImg={fallbackImg}
              hasImages={hasImages}
              vertical={vertical}
              icid={`${icid}|${presentationStyle}|pos${idx + 1}`}
              presentationStyle={presentationStyle}
              position={idx + 1}
            />
          ))
        ) : (
        // Mobile: sliding track (pages of two)
          <div className={styles.track} aria-live="polite">
            {mobileSlides.map((page, i) => (
              <div className={styles.slide} key={`slide-${i + 1}`}>
                {page.map((article, idx) => (
                  <CarouselCard
                    key={article.id}
                    article={article}
                    fallbackImg={fallbackImg}
                    hasImages={hasImages}
                    vertical={vertical}
                    icid={`${icid}|${presentationStyle}| pos${idx + 1}`}
                    presentationStyle={presentationStyle}
                    position={idx + 1}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {isMobile && totalSlides > 1 && (
        <div className={styles.carouselDots}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              type="button"
              aria-label="Pagination"
              key={`${index + 1}/${totalSlides}`}
              className={`${styles.carouselDot} ${
                currentSlide === index ? styles.active : styles.inactive
              }`}
              onClick={() => {
                goToSlide(index);
                $t('track', 'mbt_bacon_click', {
                  action: 'navClick',
                  type: `pagingDotNumber: ${index + 1}`,
                  presentation: presentationStyle,
                  title,
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { Carousel };
